import React, { useState, useEffect } from 'react';
import cx from 'classnames';
import Image from "next/image";
import { Navigation, Thumbs, Swiper as SwiperInstance, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import UIButton from 'components/Others/UIButton';
import { BackgroundColor, generateGlassesImage, generateGlassesThumbImage, generateWTBImage } from 'utils/image.utils';
import * as ga from 'lib/ga';
import 'swiper/css';
import 'swiper/css/pagination';
import ProductSpecificIcon from 'components/Others/ProductSpecificIcon';
import { useGlassesStore } from 'zus/glassesStore';
import { useVTOStore } from 'zus/vtoStore';
import { useRouter } from 'next/router';
import { imageLoader } from 'functions/imageLoader';
import Spinner from 'components/Spinner/Spinner';

const VTOModalV2 = dynamic(
  () => import('components/VTO/VTOModalV2'),
  { ssr: false }
);
import { CategoryLensTypes, FrameShape } from 'constants/dataTypes';
import dynamic from 'next/dynamic';
import { slugToString } from 'functions/slug';
import { useQueryState } from 'nuqs';

type ImageAspect = '0' | '1' | '2' | '3_model_m' | '3_model_f' | '4';

interface ProductImageSliderProps {
  category: string;
  frameColor: string;
  categoryLensTypes?: CategoryLensTypes[];
}

export default function ProductImageSlider({ category, frameColor, categoryLensTypes }: ProductImageSliderProps) {

  const [swiperInstance, setSwiperInstance] = useState<any | SwiperInstance | null>(
    null
  );
  const router = useRouter();

  const [loading, setLoading] = useState(false);

  const showVTOToggle = true;
  const isSunglasses = category.toLowerCase().includes('sunglasses');


	const [armColorQuery, setArmColorQuery] = useQueryState('armColor');
  const [lensColorQuery, setLensColorQuery] = useQueryState('lensColor');

  const armColor = armColorQuery ?? frameColor;
  const lensColor = lensColorQuery ?? (isSunglasses ? 'grey' : 'clear');
  //This should get category_display but it should not update on category change
	const isArmCustomized = router.query.armColor ? true : false; //useGlassesStore((state) => state.is_arm_customized);
	const [selectedImageIndex, setSelectedImageIndex] = useState<number>(isArmCustomized ? 2 : 0);
  
	const frameShape = useGlassesStore((state) => state.frame_shape);

  const isVTOVisible = useVTOStore((state) => state.isVisible);
  const updateVisibility = useVTOStore((state) => state.updateVisibility);
  
  const setSelectedColors = useVTOStore((state) => state.setSelectedColors);
  const setCategory = useVTOStore((state) => state.setCategory);

  const toggleVTO: () => void = () => {
    ga.sendUICustomEvent({
      eventCategory: 'VTO',
      eventAction: 'VTO Toggle Click',
      eventLabel: 'Product Detail Page'
    });

    
    setCategory(category);
    setSelectedColors(
      frameColor, 
      armColor, 
      lensColor,
      frameShape.toLowerCase() as FrameShape
    );

    updateVisibility(true);
  }
  
  const navigateToCarouselSlide = (index: number) => {
    swiperInstance?.slideTo(index + 1);
    swiperInstance?.update();

		setSelectedImageIndex(index);
  };
  
  useEffect(() => {
  
    setLoading(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lensColorQuery, armColor]);
  

	useEffect(() => {

		if (isArmCustomized) {
			swiperInstance?.slideTo(4);
			swiperInstance?.update();

			setSelectedImageIndex(3);
		} else {
			swiperInstance?.slideTo(1);
			swiperInstance?.update();

			setSelectedImageIndex(0);
		}
	}, [isArmCustomized, setSelectedImageIndex, swiperInstance]);


  let allAspects: ImageAspect[] = ['0', '3_model_f', '3_model_m', '1','2'];
  if(((router.asPath.indexOf('reading-glasses') > -1 || router.asPath.indexOf('blue-light-filter-glasses') > -1 || router.asPath.indexOf('sunglasses') > -1))){
    allAspects = ['0','3_model_f','3_model_m','1','2', '4'];
  }

  // Ensure packaging image (aspect '4') is NOT shown on prescription-sunglasses pages
  if (router.asPath.indexOf('prescription-sunglasses') > -1) {
    allAspects = allAspects.filter((aspect) => aspect !== '4');
  }

  const setFrameShape = useGlassesStore((state) => state.setFrameShape);

  useEffect(() => {
    const shape = router.query.shape as string;
    if (shape) {
      setFrameShape(shape.charAt(0).toUpperCase() + shape.slice(1) as FrameShape);
    } else {
      setFrameShape('classic');
    }
  }, [router.query.shape, setFrameShape]);

  function imageSrcGenerator(
    imageAspect: ImageAspect
  ) {
    if(imageAspect == '4'){
      const lensType = router.asPath.indexOf('reading-glasses') > -1 ? 'reading-glasses' : router.asPath.indexOf('blue-light-filter-glasses') > -1 ? 'blue-light-filter-glasses' : 'sunglasses';
      return generateWTBImage(frameColor, armColor , lensColor, lensType, frameShape);
    }
    return generateGlassesImage(
      frameColor, 
      armColor, 
      imageAspect, 
      lensColor.replace('grey-mirror', 'mirror'), 
      BackgroundColor.Grey,
      frameShape
    );
  }
  function imageThumbSrcGenerator(
    imageAspect: ImageAspect
  ) {
    if(imageAspect == '4'){
      const lensType = router.asPath.indexOf('reading-glasses') > -1 ? 'reading-glasses' : router.asPath.indexOf('blue-light-filter-glasses') > -1 ? 'blue-light-filter-glasses' : 'sunglasses';
      return generateWTBImage(frameColor, armColor , lensColor, lensType, frameShape);
    }
    return generateGlassesThumbImage(frameColor, armColor, imageAspect, lensColor, frameShape);
  }
  let slideIndex = 0;
  return (
    <div className="sticky top-[20px] z-[22] w-full xlx:static">
      <div className="group relative flex h-[420px] w-full ßitems-center justify-center bg-[#f6f8fa] xlx:mx-auto xsmx:h-[290px] xsmx:w-full">
				<ProductSpecificIcon productTitle={frameColor ?? armColorQuery ?? ''} />
    
          <Swiper
            modules={[Navigation, Pagination, Thumbs]}
            loop
            className="w-full h-[435px] xsmx:h-[305px]"
            onSwiper={setSwiperInstance}
						pagination={{
              clickable: true,
							renderBullet: (index, className) => {
								return '<span class="' + className + '">' + '</span>';
							}
						}}
            onSlideChange={(swiper) => {
              setSelectedImageIndex(swiper.realIndex);
            }}
            slidesPerView={1}>
            {allAspects.map((aspect, index) => (
              <SwiperSlide
                className={cx("relative flex !h-[420px] w-full rounded border-2 border-brand-grey125 xsmx:!h-[290px]", {
                  'bg-[#d4d4d4]': index == 5
                })}
                key={slideIndex++}>
                    {loading && (  <div className="mr-2 flex justify-center items-center pt-[1px] h-full w-full">
                      <Spinner fill="grey" />
                    </div>)}
                    <Image
                      quality={92}
                      key={`img${category}${frameColor}${armColorQuery}${lensColor}${frameShape}${index}`} 
                      fill
                          style={{
                            objectFit: index != 5 ? 'cover':'contain',
                            objectPosition: (index === 1 || index === 2) ? 'center 30%' : 'center center'
                          }}
                          sizes="540px"
                      loader={imageLoader}
                      priority
                      
                      onLoadingComplete={(image) => {
                        if(index == 0) {
                        setLoading(false);
                      }
                        image.classList.remove("opacity-0");
                      }}
                      className={cx( "transition-opacity opacity-0 duration-[0.5s]", {
                      'scale-100': index === 5})}
                      src={imageSrcGenerator(aspect)}
                      alt={`${category} ${frameColor} ${frameShape}`}
                    />
                 
              </SwiperSlide>
            ))}
          </Swiper>
        {!!showVTOToggle && (
          <div className="absolute bottom-[10px] left-[50%] z-[30] translate-x-[-50%]">
            <UIButton rounded={true} onClick={toggleVTO}>
              Virtual Try On
            </UIButton>
          </div>
        )}
        {isVTOVisible && (
          <VTOModalV2
          categoryLensTypes={categoryLensTypes}
            title={slugToString(category)}
            />
        )}
        {/* <button className="prevel absolute top-2/4 left-5 z-50  hidden h-8 w-8 -translate-y-2/4 rotate-90 transform items-center justify-center rounded-full bg-brand-grey125 opacity-50 transition-all hover:opacity-100 group-hover:flex mdx:hidden xsmx:left-3">
          <Arrow fill="#5c615cd3" width={14} height={8} />
        </button>
        <button className="nextel absolute top-2/4 right-5 z-50  hidden h-8 w-8 -translate-y-2/4 -rotate-90 transform items-center justify-center rounded-full bg-brand-grey125 opacity-50 transition-all hover:opacity-100 group-hover:flex mdx:hidden xsmx:right-3">
          <Arrow fill="#5c615cd3" width={14} height={8} />
        </button>

        <button
          onClick={() => {
            setIsImported(true);
            setModalOpen(true);
          }}
          className="absolute top-4 right-4 z-50 flex items-center justify-center">
          <Zoom />
        </button> */}

				{/* <button className="el">

				</button> */}
      </div>

      <div className="gap-2 hidden lg:flex pt-4">
        {allAspects.map((aspect, index) => (
            
            <button
              onClick={() => navigateToCarouselSlide(index)}
              key={index}
              className={cx(
								'relative h-[60px] w-[90px] rounded border-brand-grey125 bg-white',
                
								{
									'border border-[#616161] border-solid rounded-md': selectedImageIndex === index,
                  'bg-[#d4d4d4]':index == 5
								}
							)}>
              
                <Image
                  quality={80}
                  fill
                  key={`thumb${category}${frameColor}${armColorQuery}${lensColor}${frameShape}${index}`} 
                  style={{
                    objectFit: index != 5 ? 'cover':'contain',
                    objectPosition: (index === 1 || index === 2) ? 'center 30%' : 'center center'
                  }}
                  onLoadingComplete={(image) => {
                    image.classList.remove("opacity-0");
                  }}
                  loader={imageLoader}
                  sizes="88px"
                  src={imageThumbSrcGenerator(aspect)}
                  alt={`${category} ${frameColor}`}
									className={`rounded-md transition-opacity opacity-0 duration-[0.1s]`}
                  priority={true}
                />
              
            </button>
          ))}
      </div>
    </div>
  );
}
